// Recent activity — 5 most recent grid/wind sessions, using the same cards as list pages.
import { useGrids } from "@/hooks/use-grids";
import { useGridPnl } from "@/hooks/use-grid-pnl";
import { useOverview } from "@/hooks/use-overview";
import { useWindSessions } from "@/hooks/use-wind-sessions";
import GridCard from "@/components/grid/grid-card";
import WindCard from "@/components/wind/wind-card";
import type { GridConfig } from "@/types/api";
import type { UnpairedTrade } from "@/hooks/use-overview";
import type { WindSessionWithLatest } from "@/types/api";

// Per-grid PnL loader — isolates each grid's query so they cache independently.
function GridCardWithPnl({
  grid,
  unpaired,
  volatility,
}: {
  grid: GridConfig;
  unpaired: UnpairedTrade[];
  volatility: Record<string, { mode?: string; trend?: string }>;
}) {
  const { data: pnl } = useGridPnl(grid.id);
  return (
    <GridCard
      grid={grid}
      unpaired={unpaired.filter((t) => t.grid_id === grid.id)}
      pnl={pnl}
      volatility={volatility}
    />
  );
}

type Item =
  | { kind: "grid"; time: number; grid: GridConfig }
  | { kind: "wind"; time: number; wind: WindSessionWithLatest };

export default function RecentActivity() {
  const { data: grids } = useGrids();
  const { data: overview } = useOverview();
  const { data: windData } = useWindSessions();

  const unpaired = overview?.unpaired ?? [];
  const volatility = (overview?.volatility ?? {}) as Record<string, { mode?: string; trend?: string }>;

  // Merge active grids + active wind into a single timeline, sorted by created_at desc, take 5
  const items: Item[] = [];
  for (const g of grids ?? []) {
    if (g.status !== "active") continue;
    items.push({ kind: "grid", time: new Date(g.created_at).getTime(), grid: g });
  }
  for (const w of windData?.active_sessions ?? []) {
    items.push({ kind: "wind", time: new Date(w.session.created_at).getTime(), wind: w });
  }
  items.sort((a, b) => b.time - a.time);
  const recent = items.slice(0, 5);

  if (recent.length === 0) return null;

  return (
    <div className="mb-4">
      <h3 className="text-xs font-semibold text-text mb-2 px-0.5">Recent</h3>
      <div className="space-y-2">
        {recent.map((item) =>
          item.kind === "grid" ? (
            <GridCardWithPnl
              key={item.grid.id}
              grid={item.grid}
              unpaired={unpaired}
              volatility={volatility}
            />
          ) : (
            <WindCard key={item.wind.session.id} row={item.wind} />
          ),
        )}
      </div>
    </div>
  );
}
